import React from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dropzone from 'react-dropzone';
import AddUserImage from '@mui/icons-material/CloudUpload';
import FileIcon from './images/File-Icon.png';

/**
 * Pure validation helper for Terms & Conditions documents.
 * Each slot is valid when it has BOTH title and document or NEITHER.
 * A "document" is considered present when:
 * - a new file preview exists for the slot, OR
 * - an existing saved URL exists for the slot AND a non-empty title is present.
 */
export function areTermsAndConditionsSlotsValid({
  docone = '',
  doctwo = '',
  docthree = '',
  hippaPreview,
  lcPreview,
  otherPreview,
  account,
}) {
  const lc = account && account.legal_compliance;

  const title1 = (docone || '').trim();
  const title2 = (doctwo || '').trim();
  const title3 = (docthree || '').trim();

  const hasTitle1 = !!title1;
  const hasTitle2 = !!title2;
  const hasTitle3 = !!title3;

  const hasNewDoc1 =
    !!hippaPreview &&
    hippaPreview !== 'null' &&
    hippaPreview !== 'undefined';
  const hasNewDoc2 =
    !!lcPreview && lcPreview !== 'null' && lcPreview !== 'undefined';
  const hasNewDoc3 =
    !!otherPreview &&
    otherPreview !== 'null' &&
    otherPreview !== 'undefined';

  const hasExistingDoc1 = !!(lc && lc.legal_compliance_docone_url);
  const hasExistingDoc2 = !!(lc && lc.legal_compliance_doctwo_url);
  const hasExistingDoc3 = !!(lc && lc.legal_compliance_docthree_url);

  const hasDoc1 = hasNewDoc1 || (hasExistingDoc1 && hasTitle1);
  const hasDoc2 = hasNewDoc2 || (hasExistingDoc2 && hasTitle2);
  const hasDoc3 = hasNewDoc3 || (hasExistingDoc3 && hasTitle3);

  const slot1Invalid = (hasDoc1 && !hasTitle1) || (!hasDoc1 && hasTitle1);
  const slot2Invalid = (hasDoc2 && !hasTitle2) || (!hasDoc2 && hasTitle2);
  const slot3Invalid = (hasDoc3 && !hasTitle3) || (!hasDoc3 && hasTitle3);

  return !(slot1Invalid || slot2Invalid || slot3Invalid);
}

function TermsAndConditionsSection({
  readOnly,
  classes,
  t,
  patientConsent,
  onPatientConsentChange,
  contractLang,
  charCount,
  maxChar,
  onContractChange,
  docone,
  doctwo,
  docthree,
  onDocOneChange,
  onDocTwoChange,
  onDocThreeChange,
  hippaPreview,
  lcPreview,
  otherPreview,
  filename,
  lcFilename,
  otherFilename,
  onHippaDrop,
  onLCDrop,
  onOtherDrop,
  onPreviewHippa,
  onPreviewLC,
  onPreviewOther,
  onRemoveHippa,
  onRemoveLC,
  onRemoveOther,
  operation,
}) {
  return (
    <div className={classes.paddedBoxDivEdit}>
      <div
        className={classes.flexItems + ' box-container'}
        style={{ justifyContent: 'flex-start', marginTop: '15px' }}
      >
        <div className='box'>
          <div>
            <FormControlLabel
              label={
                <span style={{ color: '#000', fontSize: '1rem' }}>
                  {t('Accounts.Settings.PatientConsent')}
                </span>
              }
              labelPlacement='start'
              control={
                <Switch
                  sx={{ ml: '5.9rem' }}
                  classes={{
                    switchBase: classes.iOSSwitchBase,
                    bar: classes.iOSBar,
                    icon: classes.iOSIcon,
                    iconChecked: classes.iOSIconChecked,
                    checked: classes.iOSChecked,
                  }}
                  checked={patientConsent}
                  onChange={onPatientConsentChange}
                  value='check'
                  disabled={operation === 'View'}
                  color='primary'
                />
              }
              style={{ marginLeft: '8px', marginTop: '5px' }}
            />
          </div>
        </div>
      </div>
      {patientConsent && (
        <React.Fragment>
          <DialogTitle
            id='form-dialog-title'
            style={{ paddingBottom: '0px' }}
          >
            {t('Accounts.Terms.ContractLanguage')}
            <p className={classes.captionadvText}>
              {t('Accounts.Terms.HintText')}
            </p>
          </DialogTitle>
          <DialogContent>
            <TextField
              margin='dense'
              id='ContractLanguage'
              inputProps={{
                readOnly: Boolean(readOnly),
                maxLength: maxChar,
              }}
              label={t('Accounts.Terms.ContractLanguageText')}
              multiline
              rows='3'
              rowsMax='3'
              placeholder=''
              value={contractLang}
              onBlur={onContractChange}
              onChange={onContractChange}
              fullWidth
              variant='standard'
            />
            <label>
              {charCount}/{maxChar}
            </label>
          </DialogContent>
          <DialogTitle id='form-dialog-title'>
            {t('Accounts.Terms.TermsAndConditions')}
            {!readOnly ? (
              <p className={classes.captionadvText}>
                {t('Accounts.Terms.Note')}
              </p>
            ) : (
              ''
            )}
          </DialogTitle>
          <DialogContent>
            <div className={classes.flexItems + ' box-container'}>
              {/* First document slot */}
              <div className='box'>
                <div>
                  <TextField
                    className={classes.oneThirdTextField}
                    margin='normal'
                    id='docone'
                    inputProps={{
                      readOnly: Boolean(readOnly),
                      maxLength: 50,
                    }}
                    label={t('Accounts.Terms.TitleOfDocument')}
                    placeholder={t('Accounts.Terms.TitleOfDocument')}
                    value={docone}
                    onBlur={onDocOneChange}
                    onChange={onDocOneChange}
                    variant='standard'
                  />
                </div>
                <div className={classes.advdropbox}>
                  {readOnly ? (
                    <div>
                      {(!hippaPreview ||
                        hippaPreview === null ||
                        hippaPreview === 'null' ||
                        hippaPreview === undefined ||
                        hippaPreview === 'undefined') && (
                          <AddUserImage className={classes.dropImage} />
                        )}
                      {hippaPreview && (
                        <img
                          alt=''
                          src={FileIcon}
                          style={{
                            width: '50%',
                            height: 'auto',
                            marginTop: '10%',
                          }}
                          className={classes.dropPic}
                        />
                      )}
                    </div>
                  ) : (
                    <Dropzone
                      id='Hippadropzone'
                      onDrop={onHippaDrop}
                      className={classes.innerBox}
                    >
                      {(!hippaPreview ||
                        hippaPreview === null ||
                        hippaPreview === 'null' ||
                        hippaPreview === undefined ||
                        hippaPreview === 'undefined') && (
                          <AddUserImage className={classes.dropImage} />
                        )}
                      {hippaPreview && (
                        <img
                          alt=''
                          src={FileIcon}
                          style={{
                            width: '50%',
                            height: 'auto',
                            marginTop: '10%',
                          }}
                          className={classes.dropPic}
                        />
                      )}
                    </Dropzone>
                  )}
                </div>
                <div
                  variant='caption'
                  style={{ marginTop: '25px', textAlign: 'center' }}
                >
                  {filename !== '' ? <p>{filename}</p> : ''}
                  {hippaPreview === null ||
                    hippaPreview === 'null' ||
                    hippaPreview === undefined ||
                    hippaPreview === 'undefined' ? (
                    <p className={classes.captionadvText}>
                      {t('Accounts.Terms.UploadDocument')}
                    </p>
                  ) : (
                    <Button
                      className={classes.btnremove}
                      onClick={onPreviewHippa}
                    >
                      {t('Accounts.Terms.Preview')}
                    </Button>
                  )}
                  {hippaPreview === null ||
                    hippaPreview === 'null' ||
                    hippaPreview === undefined ||
                    hippaPreview === 'undefined'
                    ? ''
                    : (
                      <Button
                        className={classes.btnremove}
                        onClick={onRemoveHippa}
                      >
                        {t('Accounts.Terms.RemoveDocument')}
                      </Button>
                    )}
                </div>
              </div>

              {/* Second document slot */}
              <div className='box'>
                <div>
                  <TextField
                    className={classes.oneThirdTextField}
                    margin='normal'
                    id='doctwo'
                    inputProps={{
                      readOnly: Boolean(readOnly),
                      maxLength: 50,
                    }}
                    label={t('Accounts.Terms.TitleOfDocument')}
                    placeholder=''
                    value={doctwo}
                    onBlur={onDocTwoChange}
                    onChange={onDocTwoChange}
                    variant='standard'
                  />
                </div>
                <div className={classes.advdropbox}>
                  {readOnly ? (
                    <div>
                      {(!lcPreview ||
                        lcPreview === null ||
                        lcPreview === 'null' ||
                        lcPreview === undefined ||
                        lcPreview === 'undefined') && (
                          <AddUserImage className={classes.dropImage} />
                        )}
                      {lcPreview && (
                        <img
                          alt=''
                          src={FileIcon}
                          style={{
                            width: '50%',
                            height: 'auto',
                            marginTop: '10%',
                          }}
                          className={classes.dropPic}
                        />
                      )}
                    </div>
                  ) : (
                    <Dropzone
                      id='LegalConsentdropzone'
                      onDrop={onLCDrop}
                      className={classes.innerBox}
                    >
                      {(!lcPreview ||
                        lcPreview === null ||
                        lcPreview === 'null' ||
                        lcPreview === undefined ||
                        lcPreview === 'undefined') && (
                          <AddUserImage className={classes.dropImage} />
                        )}
                      {lcPreview && (
                        <img
                          alt=''
                          src={FileIcon}
                          style={{
                            width: '50%',
                            height: 'auto',
                            marginTop: '10%',
                          }}
                          className={classes.dropPic}
                        />
                      )}
                    </Dropzone>
                  )}
                </div>
                <div
                  variant='caption'
                  style={{ marginTop: '25px', textAlign: 'center' }}
                >
                  {lcFilename !== '' ? <p>{lcFilename}</p> : ''}
                  {lcPreview === null ||
                    lcPreview === 'null' ||
                    lcPreview === undefined ||
                    lcPreview === 'undefined' ? (
                    <p className={classes.captionadvText}>
                      {t('Accounts.Terms.UploadDocument')}
                    </p>
                  ) : (
                    <Button
                      className={classes.btnremove}
                      onClick={onPreviewLC}
                    >
                      {t('Accounts.Terms.Preview')}
                    </Button>
                  )}
                  {lcPreview === null ||
                    lcPreview === 'null' ||
                    lcPreview === undefined ||
                    lcPreview === 'undefined'
                    ? ''
                    : (
                      <Button
                        className={classes.btnremove}
                        onClick={onRemoveLC}
                      >
                        {t('Accounts.Terms.RemoveDocument')}
                      </Button>
                    )}
                </div>
              </div>

              {/* Third document slot */}
              <div className='box'>
                <div>
                  <TextField
                    className={classes.oneThirdTextField}
                    margin='normal'
                    id='docthree'
                    inputProps={{
                      readOnly: Boolean(readOnly),
                      maxLength: 50,
                    }}
                    label={t('Accounts.Terms.TitleOfDocument')}
                    placeholder=''
                    value={docthree}
                    onBlur={onDocThreeChange}
                    onChange={onDocThreeChange}
                    variant='standard'
                  />
                </div>
                <div className={classes.advdropbox}>
                  {readOnly ? (
                    <div>
                      {(!otherPreview ||
                        otherPreview === null ||
                        otherPreview === 'null' ||
                        otherPreview === undefined ||
                        otherPreview === 'undefined') && (
                          <AddUserImage className={classes.dropImage} />
                        )}
                      {otherPreview && (
                        <img
                          alt=''
                          src={FileIcon}
                          style={{
                            width: '50%',
                            height: 'auto',
                            marginTop: '10%',
                          }}
                          className={classes.dropPic}
                        />
                      )}
                    </div>
                  ) : (
                    <Dropzone
                      id='Otherdropzone'
                      onDrop={onOtherDrop}
                      className={classes.innerBox}
                    >
                      {(!otherPreview ||
                        otherPreview === null ||
                        otherPreview === 'null' ||
                        otherPreview === undefined ||
                        otherPreview === 'undefined') && (
                          <AddUserImage className={classes.dropImage} />
                        )}
                      {otherPreview && (
                        <img
                          alt=''
                          src={FileIcon}
                          style={{
                            width: '50%',
                            height: 'auto',
                            marginTop: '10%',
                          }}
                          className={classes.dropPic}
                        />
                      )}
                    </Dropzone>
                  )}
                </div>
                <div
                  variant='caption'
                  style={{ marginTop: '25px', textAlign: 'center' }}
                >
                  {otherFilename !== '' ? <p>{otherFilename}</p> : ''}
                  {otherPreview === null ||
                    otherPreview === 'null' ||
                    otherPreview === undefined ||
                    otherPreview === 'undefined' ? (
                    <p className={classes.captionadvText}>
                      {t('Accounts.Terms.UploadDocument')}
                    </p>
                  ) : (
                    <Button
                      className={classes.btnremove}
                      onClick={onPreviewOther}
                    >
                      {t('Accounts.Terms.Preview')}
                    </Button>
                  )}
                  {otherPreview === null ||
                    otherPreview === 'null' ||
                    otherPreview === undefined ||
                    otherPreview === 'undefined'
                    ? ''
                    : (
                      <Button
                        className={classes.btnremove}
                        onClick={onRemoveOther}
                      >
                        {t('Accounts.Terms.RemoveDocument')}
                      </Button>
                    )}
                </div>
              </div>
            </div>
          </DialogContent>
        </React.Fragment>
      )}
    </div>
  );
}

export default TermsAndConditionsSection;




// WEBPACK FOOTER //
// ./src/components/account-list/TermsAndConditionsSection.js